<template>
  <div class="d-flex flex-column h-100 frost-dates-editor">
   
    <div class="d-flex flex-row align-items-center justify-content-between ">
       <h4>Frost Dates Configuration</h4>
      <button class="btn btn-link p-0 text-primary text-decoration-none" @click="emitClearGoal">
        <i class="bi bi-x-lg"></i> Clear Goal
      </button>
    </div>
    <p class="text-muted">Define temperature thresholds for frost date analysis. Thresholds are automatically sorted from lowest to highest temperature.</p>

    <div class="mb-3">
      <h6>Frost Thresholds</h6>
      
      <div v-for="(threshold, index) in thresholds" :key="threshold.id || index" class="row mb-2 align-items-center">
        <div class="col-4">
          <input 
            v-model="threshold.label" 
            type="text" 
            class="form-control form-control-sm" 
            placeholder="e.g., light, hard"
            @input="threshold.isNew ? null : emitDefinition()"
            @blur="threshold.isNew ? null : sortAndEmit()"
          />
        </div>
        <div class="col-4">
          <div class="input-group input-group-sm">
            <input 
              v-model.number="threshold.temperature" 
              type="number" 
              step="0.1"
              class="form-control" 
              placeholder="Temperature"
              @input="threshold.isNew ? null : emitDefinition()"
              @blur="threshold.isNew ? null : sortAndEmit()"
            />
            <span class="input-group-text">°{{ temperatureUnit }}</span>
          </div>
        </div>
        <div class="col-4">
          <!-- Apply button for new items -->
          <button 
            v-if="threshold.isNew"
            class="btn btn-success btn-sm me-2" 
            @click="applyNewThreshold(index)"
            :disabled="!threshold.label || threshold.temperature === null || threshold.temperature === undefined"
            title="Apply new threshold"
          >
            <i class="bi bi-check"></i> Apply
          </button>
          
          <!-- Remove button -->
          <button 
            class="btn btn-sm btn-outline-primary mt-1" 
            @click="removeThreshold(index)"
            :disabled="thresholds.length <= 1"
            title="Remove threshold"
            style="padding: 0.125rem 0.25rem;"
          >
            <i class="bi bi-trash" style="font-size: 0.75rem;"></i>
          </button>
        </div>
      </div>

      <div class="mt-3">
        <button 
          class="btn btn-outline-primary btn-sm" 
          @click="addThreshold"
          :disabled="thresholds.length >= 5 || hasUnappliedNew"
        >
          <i class="bi bi-plus"></i> Add Threshold
        </button>
        <small class="text-muted ms-2">
          ({{ appliedThresholds.length }}/5 thresholds)
        </small>
        <div v-if="hasUnappliedNew" class="text-warning small mt-1">
          Please apply the new threshold before adding another one.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FrostDatesEditor',
  props: {
    definition: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      thresholds: []
    }
  },
  computed: {
    unitGroup() {
      return this.definition && this.definition.unitGroup;
    },
    temperatureUnit() {
      return this.unitGroup === 'us' ? 'F' : 'C';
    },
    appliedThresholds() {
      return this.thresholds.filter(t => !t.isNew);
    },
    hasUnappliedNew() {
      return this.thresholds.some(t => t.isNew);
    },
    sortedThresholds() {
      return [...this.appliedThresholds].sort((a, b) => a.temperature - b.temperature);
    },
    formattedJson() {
      return JSON.stringify(this.sortedThresholds, null, 2);
    }
  },
  methods: {
    initializeThresholds() {
      // Check if definition already has thresholds
      if (this.definition && this.definition.thresholds && Array.isArray(this.definition.thresholds) && this.definition.thresholds.length > 0) {
        return this.definition.thresholds.map(t => ({ ...t, isNew: false }));
      }
      
      // Default thresholds based on unitGroup
      const defaults = this.unitGroup === 'us'
        ? [
            { label: "Light", temperature: 32 },
            { label: "Hard", temperature: 28 }
          ]
        : [
            { label: "Light", temperature: 0 },
            { label: "Hard", temperature: -2 }
          ];
      
      return defaults.map(t => ({ ...t, isNew: false }));
    },
    addThreshold() {
      if (this.thresholds.length < 5 && !this.hasUnappliedNew) {
        // Find a reasonable default temperature
        const temps = this.appliedThresholds.map(t => t.temperature).sort((a, b) => a - b);
        const defaultTemp = temps.length > 0 ? Math.min(...temps) - (this.unitGroup === 'us' ? 5 : 3) : (this.unitGroup === 'us' ? 32 : 0);
        
        this.thresholds.push({
          id: Date.now(),
          label: "",
          temperature: defaultTemp,
          isNew: true
        });
      }
    },
    applyNewThreshold(index) {
      const threshold = this.thresholds[index];
      if (threshold && threshold.isNew && threshold.label && (threshold.temperature !== null && threshold.temperature !== undefined)) {
        // Mark as applied
        threshold.isNew = false;
        
        // Sort all thresholds and update the array
        this.sortThresholds();
        this.emitDefinition();
      }
    },
    removeThreshold(index) {
      if (this.thresholds.length > 1) {
        this.thresholds.splice(index, 1);
        this.emitDefinition();
      }
    },
    sortThresholds() {
      // Sort the thresholds array in place, keeping new items at the end
      const applied = this.thresholds.filter(t => !t.isNew).sort((a, b) => a.temperature - b.temperature);
      const newItems = this.thresholds.filter(t => t.isNew);
      this.thresholds = [...applied, ...newItems];
    },
    sortAndEmit() {
      this.sortThresholds();
      this.emitDefinition();
    },
    emitClearGoal() {
      this.$emit('clearGoal')
    },
    emitDefinition() {
      // Emit only the applied thresholds (sorted)
      this.$emit('update', { 
        thresholds: this.sortedThresholds,
        unitGroup: this.unitGroup
      });
    }
  },
  mounted() {
    // Initialize thresholds after component is mounted and computed properties are available
    this.thresholds = this.initializeThresholds();
    this.emitDefinition();
  },
  watch: {
    'definition.thresholds': {
      handler(newThresholds) {
        if (newThresholds && Array.isArray(newThresholds)) {
          this.thresholds = newThresholds.map(t => ({ ...t, isNew: false }));
        }
      },
      deep: true
    },
    unitGroup: {
      handler(newUnitGroup, oldUnitGroup) {
        if (oldUnitGroup !== undefined && newUnitGroup !== oldUnitGroup && 
            (!this.definition.thresholds || this.definition.thresholds.length === 0)) {
          this.thresholds = this.initializeThresholds();
          this.emitDefinition();
        }
      }
    }
  }
}
</script>

<style scoped>
.frost-dates-editor {
  max-width: 700px;
  margin: auto;
}

.form-control-sm {
  font-size: 0.875rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
}

.input-group-text {
  font-size: 0.875rem;
}
</style>
